export const DEFAULT_PLACEHOLDER_IMAGE = 'assets/default-service.png';

/**
 * Remote Configuration Interface
 * Defines the structure of configuration fetched from the server
 */
export interface RemoteConfig {
  version: string;
  lastUpdated: string;
  
  // Feature Flags
  features: {
    ai: {
      enabled: boolean;
      aiChatEnabled: boolean;
      aiRecommendationsEnabled: boolean;
      aiSearchEnabled: boolean;
    };
    messaging: {
      enabled: boolean;
      showVoiceMessages: boolean;
      showFileAttachments: boolean;
      showImageAttachments: boolean;
      showVideoMessages: boolean;
      showLocationSharing: boolean;
      // Attachment options
      showCameraOption?: boolean;
      showPhotoLibraryOption?: boolean;
      showAudioOption?: boolean;
      showDocumentOption?: boolean;
      showAllFilesOption?: boolean;
      maxFileSize: number; // in MB
    };
    payments: {
      enabled: boolean;
      paystackEnabled: boolean;
      walletEnabled: boolean;
      cardStorageEnabled: boolean;
    };
    bookings: {
      enabled: boolean;
      instantBookingEnabled: boolean;
      scheduleBookingEnabled: boolean;
      recurringBookingEnabled: boolean;
    };
    social: {
      googleLoginEnabled: boolean;
      appleLoginEnabled: boolean;
      guestModeEnabled: boolean;
    };
    notifications: {
      pushEnabled: boolean;
      emailEnabled: boolean;
      smsEnabled: boolean;
    };
    maps: {
      enabled: boolean;
      showProviderLocations: boolean;
      showServiceAreas: boolean;
    };
  };
  
  // UI Customization
  ui: {
    theme: 'light' | 'dark' | 'auto';
    primaryColor?: string;
    showRatings: boolean;
    showReviews: boolean;
    showProviderBadges: boolean;
    maxSearchResults: number;
    placeholderImageUrl: string;
  };
  
  // Business Rules
  business: {
    minimumBookingAmount: number;
    maximumBookingAmount: number;
    cancellationWindowHours: number;
    refundPolicy: 'full' | 'partial' | 'none';
    serviceAreaRadius: number; // in km
  };
  
  // Maintenance Mode
  maintenance: {
    enabled: boolean;
    message?: string;
    allowedUserRoles?: string[];
  };
}

/**
 * Default configuration (fallback if server is unreachable)
 */
export const DEFAULT_CONFIG: RemoteConfig = {
  version: '1.0.0',
  lastUpdated: new Date().toISOString(),
  
  features: {
    ai: {
      enabled: true,
      aiChatEnabled: true,
      aiRecommendationsEnabled: true,
      aiSearchEnabled: true,
    },
    messaging: {
      enabled: true,
      showVoiceMessages: true,
      showFileAttachments: true,
      showImageAttachments: true,
      showVideoMessages: false,
      showLocationSharing: true,
      // Attachment options - defaults: camera and photo library only
      showCameraOption: true,
      showPhotoLibraryOption: true,
      showAudioOption: false,
      showDocumentOption: false,
      showAllFilesOption: false,
      maxFileSize: 50, // 50MB — backend compresses on receipt
    },
    payments: {
      enabled: true,
      paystackEnabled: true,
      walletEnabled: true,
      cardStorageEnabled: true,
    },
    bookings: {
      enabled: true,
      instantBookingEnabled: true,
      scheduleBookingEnabled: true,
      recurringBookingEnabled: false,
    },
    social: {
      googleLoginEnabled: true,
      appleLoginEnabled: true,
      guestModeEnabled: false,
    },
    notifications: {
      pushEnabled: true,
      emailEnabled: true,
      smsEnabled: false,
    },
    maps: {
      enabled: true,
      showProviderLocations: true,
      showServiceAreas: true,
    },
  },
  
  ui: {
    theme: 'auto',
    showRatings: true,
    showReviews: true,
    showProviderBadges: true,
    maxSearchResults: 50,
    placeholderImageUrl: DEFAULT_PLACEHOLDER_IMAGE,
  },
  
  business: {
    minimumBookingAmount: 100,
    maximumBookingAmount: 50000,
    cancellationWindowHours: 24,
    refundPolicy: 'partial',
    serviceAreaRadius: 50, // 50km
  },
  
  maintenance: {
    enabled: false,
  },
};
